import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { logger } from '../utils/logger';
import {
  Typography,
  Button,
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmailIcon from '@mui/icons-material/Email';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import WarningIcon from '@mui/icons-material/Warning';
import { useAuth } from '../contexts/AuthContext';
import { testAPI, questionAPI, interviewAPI, Test, Question, Interview } from '../services/apiService';
import { questionOrdering } from '../utils/localStorage';
import WysiwygInput from '../components/WysiwygInput';
import EmailPreview from '../components/EmailPreview';
import UnlockApprovalWorkflow from '../components/UnlockApprovalWorkflow';
import { useDrag } from '../contexts/DragContext';

// Local storage constants
const LOCAL_STORAGE_QUESTIONS_CACHE_KEY = 'interviewzen-questions-cache';
const CACHE_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

// Styled components for questions
const QuestionCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isDragging' && prop !== 'isTemplate'
})<{ isDragging?: boolean; isTemplate?: boolean }>(({ theme, isDragging, isTemplate }) => ({
  marginBottom: theme.spacing(2),
  backgroundColor: isTemplate ? '#f8f9fa' : '#ffffff',
  border: isTemplate ? '2px dashed #0985E3' : '1px solid #e0e0e0',
  transform: isDragging ? 'scale(1.02)' : 'scale(1)',
  zIndex: isDragging ? 1000 : 1,
  boxShadow: isDragging 
    ? '0px 8px 16px rgba(0, 0, 0, 0.2)' 
    : '0px 2px 4px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease-in-out',
  
  '&:hover': {
    transform: isDragging ? 'scale(1.02)' : 'scale(1.01)',
    boxShadow: isDragging 
      ? '0px 8px 16px rgba(0, 0, 0, 0.2)' 
      : '0px 4px 8px rgba(0, 0, 0, 0.15)',
  }
}));

const EmptyQuestionsBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  backgroundColor: '#f8f9fa',
  borderRadius: theme.spacing(1),
  border: '2px dashed #e0e0e0',
  marginTop: theme.spacing(2)
}));

// Template question interface for inline creation
interface TemplateQuestion {
  id: string;
  content: string;
  is_html: boolean;
  order: number;
  isTemplate: true;
}

type QuestionOrTemplate = Question | TemplateQuestion;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const TestDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setError, user, refreshUserProfile } = useAuth();
  const { registerHandler, unregisterHandler } = useDrag();

  // Early return if route parameter is missing or invalid
  if (!id || id === 'undefined' || id === 'null') {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <Alert severity="error">Invalid test ID provided</Alert>
          <Button
            onClick={() => navigate('/tests')}
            variant="contained"
            sx={{ mt: 2 }}
          >
            Back to Tests
          </Button>
        </Box>
      </Container>
    );
  }
  
  const [currentTest, setCurrentTest] = useState<Test | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [orderedQuestions, setOrderedQuestions] = useState<QuestionOrTemplate[]>([]);
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [testLoading, setTestLoading] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [interviewsLoading, setInterviewsLoading] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [questionsError, setQuestionsError] = useState<string | null>(null);
  const [interviewsError, setInterviewsError] = useState<string | null>(null);
  
  const [tabValue, setTabValue] = useState(0);
  const [editingTestTitle, setEditingTestTitle] = useState(false);
  const [testName, setTestName] = useState('');
  const [editingTimeLimit, setEditingTimeLimit] = useState(false);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState<number>(45);
  
  // Question management state
  const [templateQuestion, setTemplateQuestion] = useState<TemplateQuestion | null>(null);
  const [isCreatingQuestion, setIsCreatingQuestion] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [questionToDelete, setQuestionToDelete] = useState<Question | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isSavingQuestion, setIsSavingQuestion] = useState(false);
  
  
  
  // Invitation modal state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [candidateName, setCandidateName] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  // Unlock approval workflow state
  const [unlockWorkflowOpen, setUnlockWorkflowOpen] = useState(false);
  const [selectedUnlockInterview, setSelectedUnlockInterview] = useState<Interview | null>(null);

  // Delete interview state
  const [deleteInterviewDialogOpen, setDeleteInterviewDialogOpen] = useState(false);
  const [interviewToDelete, setInterviewToDelete] = useState<Interview | null>(null);
  const [deletingInterview, setDeletingInterview] = useState(false);

  // Local storage helper functions for questions
  const getCacheKey = (testId: string) => `${LOCAL_STORAGE_QUESTIONS_CACHE_KEY}-${testId}`;

  const loadCachedQuestions = (testId: string): { questions: Question[], timestamp: number } | null => {
    try {
      const cachedData = localStorage.getItem(getCacheKey(testId));
      if (cachedData) {
        return JSON.parse(cachedData);
      }
      return null;
    } catch (error) {
      console.error('Error loading cached questions from local storage:', error);
      return null;
    }
  };

  const saveQuestionsToCache = (testId: string, questionsToCache: Question[]) => {
    try {
      const cacheData = {
        questions: questionsToCache,
        timestamp: Date.now()
      };
      localStorage.setItem(getCacheKey(testId), JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving questions to cache:', error);
    }
  };

  const isCacheValid = (timestamp: number): boolean => {
    return Date.now() - timestamp < CACHE_MAX_AGE_MS;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      // Fetch test
      setTestLoading(true);
      setTestError(null);
      try {
        const test = await testAPI.getById(id);
        setCurrentTest(test);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch test';
        setTestError(errorMessage);
        setError(errorMessage);
      } finally {
        setTestLoading(false);
      }
      
      // Fetch questions with cache-first approach
      setQuestionsError(null);
      
      // Try to load from cache first
      const cachedData = loadCachedQuestions(id);
      if (cachedData && isCacheValid(cachedData.timestamp)) {
        // Use cached data immediately
        setQuestions(cachedData.questions);
        setQuestionsLoading(false);
        
        // Still refresh in background
        try {
          const questionsList = await questionAPI.getByTestUuid(id);
          setQuestions(questionsList);
          saveQuestionsToCache(id, questionsList);
        } catch (bgError) {
          console.error('Background questions refresh failed:', bgError);
        }
      } else {
        // Cache invalid or doesn't exist, do a regular fetch
        setQuestionsLoading(true);
        try {
          const questionsList = await questionAPI.getByTestUuid(id);
          setQuestions(questionsList);
          saveQuestionsToCache(id, questionsList);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch questions';
          setQuestionsError(errorMessage);
          setError(errorMessage);
        } finally {
          setQuestionsLoading(false);
        }
      }
      
      // Fetch interviews
      setInterviewsLoading(true);
      setInterviewsError(null);
      try {
        const interviewsList = await interviewAPI.getByTestUuid(id);
        setInterviews(interviewsList);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch interviews';
        setInterviewsError(errorMessage);
        setError(errorMessage);
      } finally {
        setInterviewsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (currentTest) {
      setTestName(currentTest.name);
      setTimeLimitMinutes(currentTest.time_limit_minutes || 45);
    }
  }, [currentTest, id]);

  // Apply localStorage ordering when questions change
  useEffect(() => {
    if (!id) return;
    
    if (questions.length > 0) {
      const orderedQuestionsList = questionOrdering.applyOrder(id, questions);
      const questionsWithTemplate = templateQuestion 
        ? [templateQuestion, ...orderedQuestionsList] 
        : orderedQuestionsList;
      setOrderedQuestions(questionsWithTemplate);
    } else {
      const questionsWithTemplate = templateQuestion ? [templateQuestion] : [];
      setOrderedQuestions(questionsWithTemplate);
    }
  }, [questions, templateQuestion, id]);

  // Define drag handler before registering it
  const handleDragEnd = useCallback((result: DropResult) => {
    if (!id) return;
    
    const { source, destination } = result;

    // Dropped outside the list or no change
    if (!destination || source.index === destination.index) {
      return;
    }

    // Skip template items in drag calculations
    const realQuestions = orderedQuestions.filter(q => !('isTemplate' in q)) as Question[];
    const templateOffset = orderedQuestions.length - realQuestions.length;
    
    // Adjust indices to account for template
    const realSourceIndex = source.index - templateOffset;
    const realDestinationIndex = destination.index - templateOffset;
    
    if (realSourceIndex < 0 || realDestinationIndex < 0) {
      return; // Trying to drag template or invalid index
    }

    // Update localStorage order
    const currentOrder = questionOrdering.getOrder(id);
    if (currentOrder.length === 0) {
      // Initialize with current question order
      const initialOrder = realQuestions.map(q => q.id);
      questionOrdering.setOrder(id, initialOrder);
    }
    
    questionOrdering.reorderQuestions(id, realSourceIndex, realDestinationIndex);

    // Update local state to trigger re-render
    const newOrderedQuestions = questionOrdering.applyOrder(id, questions);
    const questionsWithTemplate = templateQuestion 
      ? [templateQuestion, ...newOrderedQuestions] 
      : newOrderedQuestions;
    setOrderedQuestions(questionsWithTemplate);
  }, [orderedQuestions, templateQuestion, questions, id]);

  // Register drag handler for this specific test's questions
  useEffect(() => {
    if (!id) return;
    
    const droppableId = `questions-list-${id}`;
    registerHandler(droppableId, handleDragEnd);
    
    return () => {
      unregisterHandler(droppableId);
    };
  }, [id, registerHandler, unregisterHandler, handleDragEnd]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleTestNameChange = async (newName: string) => {
    if (!id || !newName.trim() || newName === currentTest?.name) {
      return;
    }

    try {
      const updatedTest = await testAPI.update(id, { 
        name: newName.trim(),
        time_limit_minutes: currentTest?.time_limit_minutes || 45
      });
      setCurrentTest(updatedTest);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error updating test';
      setError(errorMessage);
      console.error('Error updating test:', error);
    }
  };

  const handleTimeLimitChange = async (newTimeLimit: number) => {
    if (!id || !currentTest || newTimeLimit === currentTest.time_limit_minutes) {
      return;
    }

    try {
      const updatedTest = await testAPI.update(id, { 
        name: currentTest.name,
        time_limit_minutes: newTimeLimit
      });
      setCurrentTest(updatedTest);
      setEditingTimeLimit(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error updating time limit';
      setError(errorMessage);
      console.error('Error updating time limit:', error);
    }
  };

  const handleCreateQuestion = () => {
    logger.debug('handleCreateQuestion called', { isCreatingQuestion, id, questionsLength: questions.length });
    
    if (isCreatingQuestion || !id) {
      logger.debug('handleCreateQuestion blocked', { isCreatingQuestion, id });
      return;
    }
    
    // Check if we've reached the 5 question limit
    if (questions.length >= 5) {
      logger.warn('Question limit reached', { questionsLength: questions.length });
      setError('Maximum of 5 questions allowed per test');
      return;
    }
    
    const newTemplate: TemplateQuestion = {
      id: `template-${Date.now()}`,
      content: '',
      is_html: false,
      order: questions.length + 1,
      isTemplate: true
    };
    
    logger.debug('Creating template question', { templateId: newTemplate.id });
    setTemplateQuestion(newTemplate);
    setIsCreatingQuestion(true);
    setEditingQuestionId(newTemplate.id);
  };

  const handleSaveNewQuestion = async (content: string) => {
    logger.debug('handleSaveNewQuestion called', { contentLength: content.length, id, isSavingQuestion });
    
    if (!content.trim() || !id || isSavingQuestion) {
      logger.debug('handleSaveNewQuestion blocked', {
        hasContent: !!content.trim(),
        hasId: !!id,
        isSavingQuestion
      });
      if (!isSavingQuestion) {
        handleCancelNewQuestion();
      }
      return;
    }

    // Double-check the 5 question limit
    if (questions.length >= 5) {
      setError('Maximum of 5 questions allowed per test');
      handleCancelNewQuestion();
      return;
    }

    setIsSavingQuestion(true);

    try {
      // Generate optimistic question with temporary ID
      const optimisticQuestion: Question = {
        id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        test_uuid: id,
        content: content.trim(),
        is_html: false,
        order: questions.length + 1
      };

      // Optimistic update: Add question to UI immediately
      setQuestions(prevQuestions => {
        const updatedQuestions = [...prevQuestions, optimisticQuestion];
        saveQuestionsToCache(id, updatedQuestions);
        return updatedQuestions;
      });

      // Add to localStorage order
      questionOrdering.addQuestion(id, optimisticQuestion.id);
      
      // Clean up template state (question now shows in list)
      setTemplateQuestion(null);
      setIsCreatingQuestion(false);
      setEditingQuestionId(null);

      // Sync with backend in background
      logger.debug('Making API call to create question', { 
        testId: id, 
        contentLength: content.trim().length, 
        order: questions.length + 1 
      });
      const createdQuestion = await questionAPI.create(id, {
        content: content.trim(),
        is_html: false,
        order: questions.length + 1,
      });
      logger.info('Question created successfully', { questionId: createdQuestion.id });
      
      if (createdQuestion) {
        // Backend success - replace optimistic question with real question
        setQuestions(prevQuestions => {
          const updatedQuestions = prevQuestions.map(q => 
            q.id === optimisticQuestion.id ? createdQuestion : q
          );
          saveQuestionsToCache(id, updatedQuestions);
          return updatedQuestions;
        });
        
        // Update localStorage order - replace temp ID with real ID
        questionOrdering.removeQuestion(id, optimisticQuestion.id);
        questionOrdering.addQuestion(id, createdQuestion.id);
      } else {
        throw new Error('Failed to create question on server');
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error creating question';
      setError(errorMessage);
      console.error('Error creating question:', error);
      
      // Revert optimistic update on error
      setQuestions(prevQuestions => {
        const revertedQuestions = prevQuestions.filter(q => !q.id.startsWith('temp-'));
        saveQuestionsToCache(id, revertedQuestions);
        return revertedQuestions;
      });
      
      // Show template again for retry
      const retryTemplate: TemplateQuestion = {
        id: `template-${Date.now()}`,
        content: content,
        is_html: false,
        order: questions.length + 1,
        isTemplate: true
      };
      setTemplateQuestion(retryTemplate);
      setIsCreatingQuestion(true);
      setEditingQuestionId(retryTemplate.id);
      
    } finally {
      setIsSavingQuestion(false);
    }
  };

  const handleCancelNewQuestion = () => {
    setTemplateQuestion(null);
    setIsCreatingQuestion(false);
    setEditingQuestionId(null);
  };

  const handleQuestionContentChange = async (question: Question, newContent: string) => {
    if (!newContent.trim() || newContent === question.content) {
      return;
    }

    try {
      const updatedQuestion = await questionAPI.update(question.id, {
        content: newContent.trim(),
        is_html: question.is_html,
        order: question.order,
      }, question.test_uuid);
      
      // Update local state
      setQuestions(prevQuestions => 
        prevQuestions.map(q => q.id === question.id ? updatedQuestion : q)
      );
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error updating question';
      setError(errorMessage);
      console.error('Error updating question:', error);
    }
  };

  const handleDeleteQuestionClick = (question: Question) => {
    setQuestionToDelete(question);
    setDeleteDialogOpen(true);
  };

  const handleDeleteQuestionConfirm = async () => {
    if (!questionToDelete || !id) return;
    
    try {
      await questionAPI.delete(questionToDelete.id, questionToDelete.test_uuid);
      
      // Remove from localStorage order
      questionOrdering.removeQuestion(id, questionToDelete.id);
      
      // Remove from local state
      setQuestions(prevQuestions => prevQuestions.filter(q => q.id !== questionToDelete.id));
      
      setDeleteDialogOpen(false);
      setQuestionToDelete(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error deleting question';
      setError(errorMessage);
      console.error('Error deleting question:', error);
    }
  };



  const handleSendInvitation = async () => {
    if (!id || !candidateName || !candidateEmail) {
      setInviteError('Name and email are required');
      return;
    }

    // Check if email already exists for this test
    const existingInterview = interviews.find(interview => 
      interview.email.toLowerCase() === candidateEmail.toLowerCase()
    );
    
    if (existingInterview) {
      setInviteError(`An interview already exists for ${candidateEmail}. Each email can only be used once per test.`);
      return;
    }

    setInviteLoading(true);
    setInviteError('');
    setInviteSuccess('');

    try {
      const result = await interviewAPI.invite(id, {
        name: candidateName,
        email: candidateEmail,
        personal_message: personalMessage,
        cc_emails: [] // Backend will auto-add manager email
      });
      
      setInviteSuccess(`Invitation sent successfully to ${candidateEmail}`);
      setCandidateName('');
      setCandidateEmail('');
      setPersonalMessage('');
      
      // Refresh interviews list to show the new invitation
      const updatedInterviews = await interviewAPI.getByTestUuid(id);
      setInterviews(updatedInterviews);
      
      // Refresh user profile to update usage counters
      try {
        await refreshUserProfile();
      } catch (profileError) {
        console.error('Error refreshing user profile:', profileError);
        // Don't fail the success flow if profile refresh fails
      }
      
      // Close modal after a short delay to show success message
      setTimeout(() => {
        setInviteModalOpen(false);
        setInviteSuccess('');
      }, 2000);
      
    } catch (error) {
      let errorMessage = 'Failed to send invitation';
      
      // Enhanced error handling for rate limiting (429 responses)
      if (error && typeof error === 'object') {
        const errorObj = error as any;
        
        // Check for 429 Too Many Requests (rate limit exceeded)
        if (errorObj.response?.status === 429 || 
            errorObj.status === 429 ||
            (errorObj.message && errorObj.message.includes('monthly invitation limit'))) {
          
          const remaining = user?.remainingUsage || 0;
          const limit = user?.monthlyLimit || 0;
          
          if (remaining <= 0 && limit > 0) {
            errorMessage = `You've reached your monthly limit of ${limit} interview invitations. Upgrade your plan to send more invitations this month.`;
          } else {
            errorMessage = 'You\'ve reached your monthly invitation limit. Please upgrade your subscription plan to send more interview invitations.';
          }
        } else if (errorObj.message) {
          errorMessage = errorObj.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setInviteError(errorMessage);
      console.error('Error sending invitation:', error);
    } finally {
      setInviteLoading(false);
    }
  };

  // Unlock approval handlers
  const handleUnlockRequest = (interview: Interview) => {
    if (!interview.unlock_requested || !interview.unlock_reason || !interview.unlock_requested_at) {
      console.error('Interview missing unlock request data');
      return;
    }
    setSelectedUnlockInterview(interview);
    setUnlockWorkflowOpen(true);
  };

  const handleUnlockWorkflowClose = () => {
    setUnlockWorkflowOpen(false);
    setSelectedUnlockInterview(null);
  };

  const handleUnlockApprovalComplete = async (newInterviewUuid?: string) => {
    // Refresh interviews list
    if (id) {
      try {
        const updatedInterviews = await interviewAPI.getByTestUuid(id);
        setInterviews(updatedInterviews);
      } catch (error) {
        console.error('Failed to refresh interviews:', error);
      }
    }
    
    handleUnlockWorkflowClose();
    
    if (newInterviewUuid) {
      // Could navigate to new interview or show success message
      logger.info('New interview created', { interviewId: newInterviewUuid });
    }
  };

  // Delete interview handlers
  const handleDeleteInterviewClick = (interview: Interview) => {
    setInterviewToDelete(interview);
    setDeleteInterviewDialogOpen(true);
  };

  const handleDeleteInterviewConfirm = async () => {
    if (!interviewToDelete || !id) return;

    setDeletingInterview(true);
    try {
      await interviewAPI.delete(interviewToDelete.uuid);
      
      // Refresh interviews list
      const updatedInterviews = await interviewAPI.getByTestUuid(id);
      setInterviews(updatedInterviews);
      
      // Close dialog and reset state
      setDeleteInterviewDialogOpen(false);
      setInterviewToDelete(null);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete interview';
      setError(errorMessage);
      console.error('Error deleting interview:', error);
    } finally {
      setDeletingInterview(false);
    }
  };

  const handleDeleteInterviewCancel = () => {
    setDeleteInterviewDialogOpen(false);
    setInterviewToDelete(null);
  };

  const handleCloseInviteModal = () => {
    setInviteModalOpen(false);
    setCandidateName('');
    setCandidateEmail('');
    setPersonalMessage('');
    setInviteError('');
    setInviteSuccess('');
  };

  const isLoading = testLoading || questionsLoading || interviewsLoading;
  const error = testError || questionsError || interviewsError;

  if (isLoading && !currentTest) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentTest && !isLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <Alert severity="error">Test not found</Alert>
          <Button
            component={RouterLink}
            to="/tests"
            variant="contained"
            sx={{ mt: 2 }}
          >
            Back to Tests
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <WysiwygInput
              value={currentTest?.name || ''}
              onChange={() => {}} // Handle change in onSave
              onSave={handleTestNameChange}
              placeholder="Test name..."
              isEditing={editingTestTitle}
              onEditingChange={(editing) => setEditingTestTitle(editing)}
              fontSize="2.125rem"
              fontWeight="400"
              showHoverIndication={!editingTestTitle}
              hoverText="Click to edit test name"
              variant="title"
            />
          </Box>
          
          {/* Time Limit Display */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Time Limit:
            </Typography>
            {editingTimeLimit ? (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={timeLimitMinutes}
                  onChange={(e: SelectChangeEvent<number>) => {
                    const newValue = e.target.value as number;
                    setTimeLimitMinutes(newValue);
                    handleTimeLimitChange(newValue);
                  }}
                  onBlur={() => setEditingTimeLimit(false)}
                  autoFocus
                >
                  <MenuItem value={30}>30 minutes</MenuItem>
                  <MenuItem value={45}>45 minutes</MenuItem>
                  <MenuItem value={60}>60 minutes</MenuItem>
                  <MenuItem value={90}>90 minutes</MenuItem>
                  <MenuItem value={120}>120 minutes</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <Chip
                label={`${currentTest?.time_limit_minutes || 45} minutes`}
                size="small"
                variant="outlined"
                onClick={() => setEditingTimeLimit(true)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              />
            )}
          </Box>
        </Box>
        
        <Paper sx={{ width: '100%' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Questions" />
            <Tab label="Interviews" />
            <Tab label="Share" />
          </Tabs>
          
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ p: 2 }}>
              {/* Header with Add Question button */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6">
                    Questions ({questions.length}/5)
                  </Typography>
                  {questions.length >= 5 && (
                    <Typography variant="caption" color="text.secondary">
                      Maximum of 5 questions reached
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleCreateQuestion}
                  disabled={isCreatingQuestion || questions.length >= 5}
                >
                  {questions.length >= 5 ? 'Maximum Reached' : 'Add Question'}
                </Button>
              </Box>

              {/* Questions List with Drag & Drop */}
              {questionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {orderedQuestions.length === 0 ? (
                    <EmptyQuestionsBox>
                      <Typography variant="h6" color="textSecondary" gutterBottom>
                        No questions yet
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Click "Add Question" above to create your first question
                      </Typography>
                    </EmptyQuestionsBox>
                  ) : (
                    <Droppable droppableId={`questions-list-${id}`}>
                      {(provided, snapshot) => (
                          <Box
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            sx={{
                              backgroundColor: snapshot.isDraggingOver ? 'rgba(9, 133, 227, 0.05)' : 'transparent',
                              transition: 'background-color 0.2s ease',
                              borderRadius: 1,
                              padding: snapshot.isDraggingOver ? 1 : 0,
                            }}
                          >
                            {orderedQuestions.map((question, index) => {
                              const isTemplate = 'isTemplate' in question;
                              const isBeingEdited = editingQuestionId === question.id;
                              
                              return (
                                <Draggable
                                  key={question.id}
                                  draggableId={question.id}
                                  index={index}
                                  isDragDisabled={isTemplate || isBeingEdited}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      style={{
                                        ...provided.draggableProps.style,
                                      }}
                                    >
                                      <QuestionCard
                                        isDragging={snapshot.isDragging}
                                        isTemplate={isTemplate}
                                      >
                                        <CardContent sx={{ position: 'relative' }}>
                                          {/* Delete Button - Only for real questions */}
                                          {!isTemplate && (
                                            <IconButton
                                              sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                zIndex: 2,
                                                padding: 0.5,
                                                transition: 'all 0.2s ease-in-out',
                                                color: 'text.secondary',
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                '&:hover': {
                                                  color: 'error.main',
                                                  backgroundColor: 'rgba(255, 255, 255, 1)',
                                                  transform: 'scale(1.1)',
                                                },
                                              }}
                                              size="small"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                handleDeleteQuestionClick(question as Question);
                                              }}
                                              aria-label="delete"
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          )}

                                          {/* Question Number */}
                                          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                                            Question {index + 1}
                                          </Typography>

                                          {/* Question Content - WYSIWYG Editable */}
                                          {isTemplate ? (
                                            <WysiwygInput
                                              value={question.content}
                                              onChange={() => {}} // Don't update template during typing, like TestsPage
                                              onSave={(content) => {
                                                logger.debug('Template question onSave called', { contentLength: content.length });
                                                handleSaveNewQuestion(content);
                                              }}
                                              onCancel={handleCancelNewQuestion}
                                              placeholder="Enter your question..."
                                              isEditing={true}
                                              onEditingChange={(editing) => {
                                                logger.debug('Template question editing changed', { editing });
                                                if (!editing) {
                                                  // When editing ends, ensure template state is cleaned up
                                                  setTemplateQuestion(null);
                                                  setIsCreatingQuestion(false);
                                                  setEditingQuestionId(null);
                                                }
                                              }}
                                              autoFocus
                                              fontSize="1rem"
                                              fontWeight="normal"
                                              variant="content"
                                              multiline={true}
                                              allowEnterToSave={true}
                                              isTemplate={true}
                                            />
                                          ) : (
                                            <WysiwygInput
                                              value={question.content}
                                              onChange={() => {}} // Handle change in onSave
                                              onSave={(newContent) => handleQuestionContentChange(question as Question, newContent)}
                                              placeholder="Question content..."
                                              isEditing={isBeingEdited}
                                              onEditingChange={(editing) => {
                                                setEditingQuestionId(editing ? question.id : null);
                                              }}
                                              fontSize="1rem"
                                              fontWeight="normal"
                                              showHoverIndication={!isBeingEdited}
                                              hoverText="Click to edit question"
                                              variant="content"
                                              multiline={true}
                                              allowEnterToSave={true}
                                            />
                                          )}

                                          {/* Template Instructions */}
                                          {isTemplate && (
                                            <Typography 
                                              variant="body2" 
                                              color="textSecondary" 
                                              sx={{ fontStyle: 'italic', mt: 1 }}
                                            >
                                              Type your question and press Enter to save
                                            </Typography>
                                          )}
                                        </CardContent>
                                      </QuestionCard>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </Box>
                        )}
                      </Droppable>
                  )}
                </>
              )}
            </Box>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Interviews
              </Typography>
              
              {interviewsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <CircularProgress />
                </Box>
              ) : interviews.length === 0 ? (
                <Typography variant="body1" color="textSecondary" align="center">
                  No interviews yet. Share your test link to start receiving interviews.
                </Typography>
              ) : (
                <Grid container spacing={3}>
                  {interviews.map((interview) => (
                    <Grid item xs={12} sm={6} md={4} key={interview.uuid}>
                      <Card sx={{ position: 'relative' }}>
                        {/* Delete Interview Button - Top Right */}
                        <Tooltip title="Delete Interview">
                          <IconButton
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 2,
                              padding: 0.5,
                              transition: 'all 0.2s ease-in-out',
                              color: 'grey.500',
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              '&:hover': {
                                color: 'error.main',
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                                transform: 'scale(1.1)',
                              },
                            }}
                            size="small"
                            onClick={() => handleDeleteInterviewClick(interview)}
                            disabled={deletingInterview}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Unlock Request Badge */}
                        {interview.unlock_requested && !interview.unlock_approved && (
                          <Chip
                            icon={<WarningIcon />}
                            label="Unlock Requested"
                            color="warning"
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 48, // Move left to avoid overlapping with delete button
                              zIndex: 1,
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.7 },
                                '100%': { opacity: 1 },
                              },
                            }}
                          />
                        )}
                        
                        <CardContent>
                          <Typography variant="h6" component="h2">
                            {interview.name}
                          </Typography>
                          <Typography color="textSecondary">
                            {interview.email}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Status: {interview.status.replace('_', ' ')}
                          </Typography>
                          
                          {/* Unlock Request Info */}
                          {interview.unlock_requested && (
                            <Alert 
                              severity={interview.unlock_approved ? "success" : "warning"}
                              size="small"
                              sx={{ mt: 2, py: 0 }}
                            >
                              <Typography variant="caption">
                                {interview.unlock_approved 
                                  ? "Unlock approved" 
                                  : "Pending unlock approval"
                                }
                              </Typography>
                            </Alert>
                          )}

                          {/* Action Buttons */}
                          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {interview.status === 'completed' && (
                              <Button
                                variant="outlined"
                                size="small"
                                component={RouterLink}
                                to={`/interviews/${interview.uuid}/results`}
                              >
                                Review Results
                              </Button>
                            )}
                            
                            {interview.unlock_requested && !interview.unlock_approved && (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<LockOpenIcon />}
                                onClick={() => handleUnlockRequest(interview)}
                                color="warning"
                                sx={{
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    transition: 'transform 0.2s ease',
                                  },
                                }}
                              >
                                Review Unlock
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Share Test
              </Typography>
              
              {/* Email Invitation Section */}
              <Paper sx={{ p: 3, bgcolor: 'background.default', mb: 3 }}>
                <Typography variant="body1" gutterBottom>
                  Send interview invitations directly via email:
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Candidates will receive an email with the interview link and client download instructions.
                </Typography>
                
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<EmailIcon />}
                    onClick={() => setInviteModalOpen(true)}
                  >
                    Invite via Email
                  </Button>
                </Box>
              </Paper>
              
            </Box>
          </TabPanel>
        </Paper>
      </Box>
      
      {/* Delete Question Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Delete Question</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this question? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteQuestionConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>


      {/* Email Invitation Dialog */}
      <Dialog
        open={inviteModalOpen}
        onClose={handleCloseInviteModal}
        aria-labelledby="invite-dialog-title"
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle id="invite-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EmailIcon sx={{ mr: 1 }} />
            Send Interview Invitation
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Enter the candidate's details to send them an interview invitation via email.
            You'll see a preview of the exact email that will be sent.
          </DialogContentText>
          
          {inviteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {inviteError}
            </Alert>
          )}
          
          {inviteSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {inviteSuccess}
            </Alert>
          )}
          
          <Grid container spacing={3}>
            {/* Form Section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ pr: { md: 2 } }}>
                <Typography variant="h6" gutterBottom>
                  Invitation Details
                </Typography>
                
                <TextField
                  autoFocus
                  margin="dense"
                  label="Candidate Name"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={candidateName}
                  onChange={(e) => setCandidateName(e.target.value)}
                  disabled={inviteLoading}
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  margin="dense"
                  label="Email Address"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={candidateEmail}
                  onChange={(e) => setCandidateEmail(e.target.value)}
                  disabled={inviteLoading}
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  margin="dense"
                  label="Personal Message (Optional)"
                  multiline
                  rows={4}
                  fullWidth
                  variant="outlined"
                  placeholder="Add a personal message to include in the invitation email..."
                  value={personalMessage}
                  onChange={(e) => setPersonalMessage(e.target.value)}
                  disabled={inviteLoading}
                />
              </Box>
            </Grid>
            
            {/* Preview Section */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Email Preview
              </Typography>
              <EmailPreview
                candidateName={candidateName}
                testName={currentTest?.name || ''}
                personalMessage={personalMessage}
                managerName={user?.name || ''}
                questionCount={questions.length}
              />
              
              {/* Show existing interviews for reference */}
              {interviews.length > 0 && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Existing Interviews ({interviews.length}):
                  </Typography>
                  <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>
                    {interviews.map((interview, index) => (
                      <Typography key={interview.uuid} variant="caption" display="block" color="textSecondary">
                        • {interview.email} ({interview.status})
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInviteModal} disabled={inviteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSendInvitation} 
            variant="contained"
            disabled={inviteLoading || !candidateName || !candidateEmail}
            startIcon={inviteLoading ? <CircularProgress size={20} /> : <EmailIcon />}
          >
            {inviteLoading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlock Approval Workflow */}
      {unlockWorkflowOpen && selectedUnlockInterview && currentTest && (
        <UnlockApprovalWorkflow
          interviewUuid={selectedUnlockInterview.uuid}
          testUuid={currentTest.uuid}
          candidateName={selectedUnlockInterview.name}
          candidateEmail={selectedUnlockInterview.email}
          testName={currentTest.name}
          unlockReason={selectedUnlockInterview.unlock_reason || ''}
          requestedAt={selectedUnlockInterview.unlock_requested_at || ''}
          onClose={handleUnlockWorkflowClose}
          onApprovalComplete={handleUnlockApprovalComplete}
        />
      )}

      {/* Delete Interview Confirmation Dialog */}
      <Dialog
        open={deleteInterviewDialogOpen}
        onClose={handleDeleteInterviewCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Interview</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the interview for <strong>{interviewToDelete?.name}</strong> ({interviewToDelete?.email})? 
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteInterviewCancel} disabled={deletingInterview}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteInterviewConfirm}
            disabled={deletingInterview}
            startIcon={deletingInterview ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deletingInterview ? 'Deleting...' : 'Delete Interview'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TestDetailPage;
